/**
 * The cycle status band's rules, as pure functions over `GET /foundations/cycle-runs` (ADR 9016).
 *
 * Every function here takes what the platform published and says what the band may state from
 * it — nothing more. Three refusals carry the design, and each has a test:
 *
 *   * **A stopped schedule is never rendered as running.** Only the orchestrator's own word
 *     `RUNNING`, on a report fresh enough to still be true and with a next tick still ahead,
 *     yields a next-run date. Every other word, a stale report and a past tick all read "state
 *     unknown since …" — the orchestrator's last word is named beside it, never mapped onto one
 *     this build knows.
 *   * **An unreported schedule is an absence, not a status** (ADR 0014). `orchestrator: null` is
 *     `notReported`, and the band names why — the orchestrator has not reported it.
 *   * **No date is made up.** The next run is `nextTickAt`, the orchestrator's computation. The
 *     cadence is rendered from the declared cron only when the cron is one of the two simple
 *     shapes the catalogue can say in words; anything else is shown as the cron itself.
 *
 * The platform publishes no staleness verdict (bata-platform ADR 6035: "a report older than its
 * interval is still returned with its `reportedAt`, so a client can say 'state unknown since …'"),
 * so the threshold is this build's, and ADR 9016 records it.
 */

import type {
  CycleRunsResponse, CycleStatus, CycleTick, CycleTickStage, FeedStatus, ModuleStatus, ModulesResponse,
  OrchestratorScheduleState, ParameterKeyEntry,
} from '@/api/generated';
import { ApiMalformedProblemError, ApiProblemError } from '@/api';
import { displayNumberFormat } from '@/lib/format';

/**
 * How old a schedule report may be and still be read as the schedule's state. The reporting
 * sensor (`bata-data` step 1.3) evaluates every 60 seconds; ten intervals is late enough that a
 * slow evaluation or a restart does not flap the band, and early enough that a dead sensor is
 * named within the quarter hour. ADR 9016.
 */
export const SCHEDULE_REPORT_STALE_MS = 10 * 60 * 1000;

/** The orchestrator's two instigator words, verbatim (bata-platform ADR 6035). */
export const ORCHESTRATOR_RUNNING = 'RUNNING';
export const ORCHESTRATOR_STOPPED = 'STOPPED';

/**
 * The cycles the band shows, in the platform's own order (bata-platform ADR 6048, DEC-04/05): the
 * in-season chain is split into the daily replenishment cycle (distribute, cost to serve,
 * rebalance) and the weekly price cycle (reforecast, replan, price); exit is unchanged. A platform
 * before v0.51 still publishes one `inseason` cycle — it is not one of these names, so each of the
 * two in-season columns says the platform does not report it rather than borrowing its state.
 */
export const CYCLE_MODULES = ['inseason_replenishment', 'inseason_price', 'exit'] as const;
export type CycleModule = (typeof CYCLE_MODULES)[number];

/**
 * The DEC-06 module that gates each cycle's stages (bata-platform ADR 1004): its pause, cadence and
 * excluded doors are registry rows, and `GET /foundations/modules` says who approved them.
 */
export const CYCLE_GATE: Readonly<Record<CycleModule, string>> = {
  inseason_replenishment: 'inseason_replenishment',
  inseason_price: 'inseason_price',
  exit: 'exit_consolidation',
};

/** When the cycle runs next, as far as the published report lets the band say. */
export type NextRun =
  /** No report has ever landed for the schedule — an absence with its reason. */
  | { readonly kind: 'notReported' }
  /** The orchestrator says `STOPPED`, on a fresh report. There is no next run. */
  | { readonly kind: 'stopped'; readonly reportedAt: string }
  /** The orchestrator says `RUNNING`, on a fresh report, with its next tick still ahead. */
  | { readonly kind: 'scheduled'; readonly at: string; readonly reportedAt: string }
  /** Anything else: a stale report, a past tick, no tick, or a word this build does not know. */
  | { readonly kind: 'unknownSince'; readonly reportedAt: string; readonly status: string };

const instant = (value: string | null): number | null => {
  if (value === null) return null;
  const at = new Date(value).getTime();
  return Number.isNaN(at) ? null : at;
};

export function nextRun(orchestrator: OrchestratorScheduleState | null, now: number): NextRun {
  if (orchestrator === null) return { kind: 'notReported' };
  const { status, reportedAt, nextTickAt } = orchestrator;
  const unknown: NextRun = { kind: 'unknownSince', reportedAt, status };
  const reported = instant(reportedAt);
  // An unparseable or stale report says nothing about now — whatever word it carries.
  if (reported === null || now - reported > SCHEDULE_REPORT_STALE_MS) return unknown;
  if (status === ORCHESTRATOR_STOPPED) return { kind: 'stopped', reportedAt };
  if (status !== ORCHESTRATOR_RUNNING) return unknown;
  const next = instant(nextTickAt);
  // A running schedule whose next tick has passed is a report the orchestrator has not
  // refreshed since the tick — it cannot say when the tick after that is.
  if (next === null || nextTickAt === null || next <= now) return unknown;
  return { kind: 'scheduled', at: nextTickAt, reportedAt };
}

/**
 * The newest tick of a cycle, as the band states it (bata-platform ADR 6043, bata-ui#166 W0-4).
 *
 * The platform decides the tick's state — `succeeded` only when every non-excluded stage
 * succeeded WITH output — and the band renders that decision, never one of its own. It replaces
 * this build's old "worst of each stage's newest run", which mixed runs of different ticks: a
 * stage that failed on Monday and a stage that succeeded on Tuesday read as one cycle, and a
 * tick whose distribute refused everything read green.
 *
 *   * `notPublished` — the read has no `tick` field: a platform before v0.50. Nothing is said
 *     about the last run rather than falling back to the old per-stage reading.
 *   * `noTick` — `tick: null`: no run of this cycle for the market ever carried a cycle tick
 *     (manual or backfill runs only). Not a failure, and never green.
 *   * `tick` — the newest tick, stage by stage, in the cycle's order.
 */
export type TickState = CycleTick['state'];
export type StageState = CycleTickStage['state'];

/** One stage of the newest tick, as the band lists it. */
export interface TickStage {
  readonly jobName: string;
  /** The platform's word, verbatim — a word this build does not know is passed through. */
  readonly state: string;
  /**
   * The code that explains the state: `failureCode` when failed, `refusalCode` when refused,
   * `skipCode` when skipped (a governed pause or a cadence, DEC-06).
   */
  readonly code: string | null;
  readonly finishedAt: string | null;
  /**
   * The keys the run itself named when it refused for `planning.parameters.missing`, recorded by
   * the runner (`CycleJobRun.missingParameters`, bata-platform ADR 9013 amended). `null` for any
   * other run and for a run recorded before the field existed; `undefined` on a platform before
   * 0.52 that does not publish it. Only an array is the run's own list.
   */
  readonly missingParameters?: readonly string[] | null | undefined;
}

export type TickSummary =
  | { readonly kind: 'notPublished' }
  | { readonly kind: 'noTick' }
  | {
    readonly kind: 'tick';
    readonly cycleTick: string;
    /** The platform's tick state, verbatim. */
    readonly state: string;
    readonly stages: readonly TickStage[];
    /** The newest `finishedAt` among the tick's stage runs, or `null` when none finished. */
    readonly finishedAt: string | null;
    /** Stages the cycle runs (every declared stage that is not excluded), and how many ran in this tick. */
    readonly counted: number;
    readonly ran: number;
    /**
     * The stage that decides the tick's state, in the cycle's order: the first `failed` stage
     * for a failed tick, the first `refused` for a refused one, the first that produced nothing
     * for `produced_nothing`, the first `skipped` for a skipped one. `null` for the other states.
     */
    readonly culprit: TickStage | null;
    /** For an `incomplete` tick: the stages with no run in this tick (`not_run`) or unclassified. */
    readonly missing: number;
  };

/**
 * Stage states that mean "this stage is not part of the tick's verdict": excluded by the cycle,
 * or skipped by its module's governed pause or cadence (the platform counts `skipped` like
 * `excluded`, ADR 1004).
 */
const NOT_COUNTED: ReadonlySet<string> = new Set(['excluded', 'skipped']);
const RAN: ReadonlySet<string> = new Set(['succeeded', 'failed', 'queued', 'running', 'refused', 'produced_nothing', 'unclassified']);

/** Which stage state names the tick state's cause. */
const CULPRIT_OF: Readonly<Record<string, string>> = {
  failed: 'failed',
  refused: 'refused',
  produced_nothing: 'produced_nothing',
  skipped: 'skipped',
};

function codeOf(stage: CycleTickStage): string | null {
  const run = stage.run;
  if (run === null) return null;
  if (stage.state === 'failed') return run.failureCode;
  if (stage.state === 'refused') return run.refusalCode ?? null;
  if (stage.state === 'skipped') return run.skipCode ?? null;
  return null;
}

function stageOf(stage: CycleTickStage): TickStage {
  return {
    jobName: stage.jobName,
    state: stage.state,
    code: codeOf(stage),
    finishedAt: stage.run?.finishedAt ?? null,
    missingParameters: stage.run === null ? null : stage.run.missingParameters,
  };
}

/**
 * Where the band names a DEC-10 refusal's keys from. `recorded`: the run's own list, verbatim and
 * sorted — no catalogue read. `inferred`: the run recorded none (a run before platform 0.52, or a
 * platform that does not publish the field), so the band falls back to the catalogue inference
 * (`unsetKeysOfStage`) and says it is one.
 */
export type MissingKeysSource =
  | { readonly kind: 'recorded'; readonly keys: readonly string[] }
  | { readonly kind: 'inferred' };

export function missingKeysSource(stage: Pick<TickStage, 'missingParameters'>): MissingKeysSource {
  const keys = stage.missingParameters;
  return Array.isArray(keys) ? { kind: 'recorded', keys: [...keys].sort() } : { kind: 'inferred' };
}

export function tickSummary(cycle: CycleStatus): TickSummary {
  if (cycle.tick === undefined) return { kind: 'notPublished' };
  if (cycle.tick === null) return { kind: 'noTick' };
  const { cycleTick, state } = cycle.tick;
  const stages = cycle.tick.stages.map(stageOf);
  const finishedAt = stages.reduce<string | null>((newest, stage) => {
    const at = instant(stage.finishedAt);
    if (at === null || stage.finishedAt === null) return newest;
    const current = instant(newest);
    return current === null || at > current ? stage.finishedAt : newest;
  }, null);
  const culpritState = CULPRIT_OF[state];
  const culprit = culpritState === undefined ? null : stages.find((stage) => stage.state === culpritState) ?? null;
  return {
    kind: 'tick',
    cycleTick,
    state,
    stages,
    finishedAt,
    counted: stages.filter((stage) => !NOT_COUNTED.has(stage.state)).length,
    ran: stages.filter((stage) => RAN.has(stage.state)).length,
    culprit,
    missing: stages.filter((stage) => stage.state === 'not_run' || stage.state === 'unclassified').length,
  };
}

/** Each stage state's words, spelled out for the catalogue check. */
const STAGE_STATE_KEY: Readonly<Record<StageState, string>> = {
  succeeded: 'overview.cycleStatus.stage.succeeded',
  failed: 'overview.cycleStatus.stage.failed',
  queued: 'overview.cycleStatus.stage.queued',
  running: 'overview.cycleStatus.stage.running',
  refused: 'overview.cycleStatus.stage.refused',
  produced_nothing: 'overview.cycleStatus.stage.producedNothing',
  unclassified: 'overview.cycleStatus.stage.unclassified',
  not_run: 'overview.cycleStatus.stage.notRun',
  excluded: 'overview.cycleStatus.stage.excluded',
  skipped: 'overview.cycleStatus.stage.skipped',
};

/** One stage's words in the band's stage list. A word this build does not know reads verbatim. */
export function stageStateKey(state: string): string {
  return (STAGE_STATE_KEY as Readonly<Record<string, string>>)[state] ?? 'overview.cycleStatus.stage.other';
}

/**
 * Codes the band can explain in words. A code not listed is still shown — as itself — so a new
 * refusal degrades to its identifier rather than to nothing.
 */
export const PARAMETERS_MISSING_CODE = 'planning.parameters.missing';

/** Each decision cycle's name — the band's row titles and the cycles workbook's words. */
export const CYCLE_MODULE_TITLE: Readonly<Record<CycleModule, string>> = {
  inseason_replenishment: 'overview.cycleStatus.module.inseasonReplenishment',
  inseason_price: 'overview.cycleStatus.module.inseasonPrice',
  exit: 'overview.cycleStatus.module.exit',
};

const CODE_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.entry_point.stale_partition': 'overview.cycleStatus.code.staleStock',
  // Platform ADR 6063: the stock day had no DC or in-transit channel, the pulls were refused, and
  // the day decided no transfer either — so the run wrote nothing.
  'planning.inseason.stock_feed_incomplete': 'overview.cycleStatus.code.stockFeedIncomplete',
  // Platform ADR 6070: the cells the pull ranged rose past the market's limit against its previous
  // complete run, the pulls were refused, and the day decided no transfer either. (A missing
  // ranging key refuses under `planning.parameters.missing`, below, naming the keys.)
  'planning.inseason.ranged_cells_rise': 'overview.cycleStatus.code.rangedCellsRise',
  // DEC-10 (bata-platform ADR 9013): a stage that writes actions refuses a missing registry row.
  [PARAMETERS_MISSING_CODE]: 'overview.cycleStatus.code.parametersMissing',
  // DEC-06 (bata-platform ADR 1004): the module's governed pause, its cadence, or a cadence the
  // runner cannot read (fail-closed).
  'platform.module.disabled': 'overview.cycleStatus.code.moduleDisabled',
  'platform.module.not_due': 'overview.cycleStatus.code.moduleNotDue',
  'platform.module.cadence_invalid': 'overview.cycleStatus.code.moduleCadenceInvalid',
};

export function codeExplanationKey(code: string | null): string | null {
  return code === null ? null : CODE_KEY[code] ?? null;
}

/**
 * Why a module is paused, as the registry says it (`GET /foundations/modules`, DEC-06): the row in
 * force, since when, and who approved it — "paused since X, approved by Y". Only what the read
 * publishes; a module the read does not list, or a read that has not answered, is `unknown`, never
 * "enabled".
 */
export type ModuleStanding =
  | { readonly kind: 'unknown' }
  | { readonly kind: 'enabled'; readonly module: ModuleStatus }
  | {
    readonly kind: 'disabled' | 'cadenceInvalid';
    readonly module: ModuleStatus;
    /** The row that decides it: `enabled` for a pause, `cadence` for an invalid cadence. */
    readonly since: string | null;
    readonly approvedBy: readonly string[];
    /** Each approver's profile name, aligned index by index with `approvedBy`; `null` where none (B6). */
    readonly approvedByNames: readonly (string | null)[];
    /** Why each approver has no name (ADR 2127), aligned the same way; `null` where a name stands. */
    readonly approvedByNamesAbsentReasons: readonly (string | null)[];
    readonly approvedAt: string | null;
    readonly source: string;
  }
  /** A word this build does not know, verbatim. */
  | { readonly kind: 'other'; readonly module: ModuleStatus; readonly state: string };

export function moduleStanding(modules: ModulesResponse | undefined, gate: string): ModuleStanding {
  const module = modules?.modules.find((entry) => entry.module === gate);
  if (module === undefined) return { kind: 'unknown' };
  if (module.state === 'enabled') return { kind: 'enabled', module };
  if (module.state === 'disabled' || module.state === 'cadence_invalid') {
    const row = module.state === 'disabled' ? module.enabled : module.cadence;
    return {
      kind: module.state === 'disabled' ? 'disabled' : 'cadenceInvalid',
      module,
      since: row.validFrom,
      approvedBy: row.approvedBy,
      approvedByNames: row.approvedByNames ?? [],
      approvedByNamesAbsentReasons: row.approvedByNamesAbsentReasons ?? [],
      approvedAt: row.approvedAt,
      source: row.source,
    };
  }
  return { kind: 'other', module, state: module.state };
}

/**
 * The keys a refused stage reads that have no row in force on the tick's date, from the
 * catalogue (`GET /registry/parameter-keys?as_of=`): `status: unset` and the stage among
 * `affectsStages`. An inference from the catalogue, stated as such — the run's own list of missing
 * keys is operator evidence the planner reads do not publish. `null` when the catalogue carries no
 * stage list (a platform before the parameter card).
 */
export function unsetKeysOfStage(entries: readonly ParameterKeyEntry[], stage: string): readonly string[] | null {
  if (!entries.some((entry) => entry.affectsStages !== undefined)) return null;
  return entries
    .filter((entry) => entry.status === 'unset' && (entry.affectsStages ?? []).includes(stage))
    .map((entry) => entry.parameterKey)
    .sort();
}

/** The tick's UTC calendar date, `YYYY-MM-DD` — the date the platform resolves the registry at. */
export function tickDate(cycleTick: string): string | null {
  const at = new Date(cycleTick);
  return Number.isNaN(at.getTime()) ? null : at.toISOString().slice(0, 10);
}

/**
 * The declared cadence in words, where the cron is one the catalogue can say: a minute and an
 * hour, every day or on one weekday. Anything else — a range, a list, a step — is shown as the
 * cron itself; rendering it in words would need a cron parser this build does not carry and
 * the platform deliberately does not run either (bata-platform ADR 6035).
 */
export type Cadence =
  | { readonly kind: 'undeclared' }
  | { readonly kind: 'daily'; readonly hour: number; readonly minute: number }
  | { readonly kind: 'weekly'; readonly weekday: number; readonly hour: number; readonly minute: number }
  | { readonly kind: 'cron'; readonly cron: string };

const SIMPLE_CRON = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+(\*|[0-7])$/;

export function cadence(declaredCron: string | null): Cadence {
  if (declaredCron === null || declaredCron.trim() === '') return { kind: 'undeclared' };
  const cron = declaredCron.trim();
  const match = SIMPLE_CRON.exec(cron);
  if (match === null) return { kind: 'cron', cron };
  const minute = Number(match[1]);
  const hour = Number(match[2]);
  if (minute > 59 || hour > 23) return { kind: 'cron', cron };
  const day = match[3];
  if (day === '*' || day === undefined) return { kind: 'daily', hour, minute };
  // Cron admits both 0 and 7 for Sunday.
  return { kind: 'weekly', weekday: Number(day) % 7, hour, minute };
}

/** The cycle named `module` in the read, or `undefined` when the platform did not publish it. */
export function cycleOf(data: CycleRunsResponse | undefined, module: CycleModule): CycleStatus | undefined {
  return data?.cycles.find((cycle) => cycle.cycle === module);
}

/**
 * Whether a failed read means "this platform does not publish cycle status" — the route is
 * absent (production answers 404 until platform v0.47 deploys) — rather than a fault. The
 * endpoint itself publishes no 404 (bata-platform ADR 6035: a country outside the grant reads
 * as nothing ran), so any 404 is the route's absence, with or without a problem document.
 */
export function isCycleStatusNotPublished(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404 || error.status === 501;
  return error instanceof ApiProblemError && (error.status === 404 || error.status === 501);
}

/**
 * Data as-of, from `GET /foundations/feeds`: the stalest feed that states its freshness, in the
 * hours the platform published. Not converted to a date — that would be this build's clock
 * minus the platform's hours, a date nobody published. `null` when no feed states it.
 */
/** Below this a feed's age is said in hours; from it, in whole days. */
export const AGE_IN_DAYS_FROM_HOURS = 48;

/**
 * A feed's age as a person reads it: «5 h» under two days, «31 giorni» from two days — rounded,
 * in the locale, through `Intl`'s units (no catalogue words). The platform publishes the age as
 * fractional hours (`751.8685033391668`), which the Cycles page printed verbatim until the
 * signed-in production check of 2026-10-02. The exact hours stay in the element's `title`.
 */
export function formatFeedAge(hours: number, locale: string): string {
  const days = hours >= AGE_IN_DAYS_FROM_HOURS;
  return displayNumberFormat(locale, {
    style: 'unit',
    unit: days ? 'day' : 'hour',
    unitDisplay: days ? 'long' : 'short',
    maximumFractionDigits: 0,
  }).format(days ? hours / 24 : hours);
}

export function stalestFeed(rows: readonly FeedStatus[]): { readonly feedId: string; readonly hours: number } | null {
  let stalest: { readonly feedId: string; readonly hours: number } | null = null;
  for (const row of rows) {
    const hours = row.freshnessHours;
    if (typeof hours !== 'number' || Number.isNaN(hours)) continue;
    if (stalest === null || hours > stalest.hours) stalest = { feedId: row.feedId, hours };
  }
  return stalest;
}
